<script setup lang="ts">
import { ref } from 'vue';
import { useToast } from 'primevue/usetoast';

const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || '';

const toast = useToast();
const popover = ref();

function togglePopover(event: Event) {
  popover.value?.toggle(event);
}

async function copyEmail() {
  try {
    await navigator.clipboard.writeText(supportEmail);
    toast.add({ severity: 'success', summary: 'Copied', detail: 'Support email copied to clipboard', life: 2000 });
  } catch {
    toast.add({ severity: 'warn', summary: 'Copy failed', detail: 'Could not copy email to clipboard', life: 2000 });
  }
}
</script>

<template>
  <div class="support-widget">
    <Button
      icon="pi pi-headphones"
      class="support-widget-button"
      rounded
      raised
      aria-label="Contact support"
      v-tooltip.left="'Contact support'"
      @click="togglePopover"
    />

    <Popover ref="popover" class="support-widget-popover">
      <div class="flex flex-col gap-3 w-64">
        <div class="flex items-center gap-2">
          <i class="pi pi-headphones text-lg" style="color: var(--primary-color)" />
          <span class="font-semibold text-color">Need help?</span>
        </div>
        <p class="text-sm text-color-secondary m-0">
          Reach out to our support team and we'll get back to you as soon as possible.
        </p>
        <div class="flex items-center justify-between gap-2 support-widget-email">
          <a :href="`mailto:${supportEmail}`" class="text-sm truncate" style="color: var(--primary-color)">
            {{ supportEmail }}
          </a>
          <Button
            icon="pi pi-copy"
            text
            rounded
            size="small"
            v-tooltip.top="'Copy email'"
            aria-label="Copy support email"
            @click="copyEmail"
          />
        </div>
        <Button
          as="a"
          :href="`mailto:${supportEmail}`"
          label="Email support"
          icon="pi pi-send"
          size="small"
        />
      </div>
    </Popover>
  </div>
</template>

<style scoped lang="scss">
.support-widget {
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  z-index: 1100;
}

.support-widget-button {
  width: 3.25rem !important;
  height: 3.25rem !important;
  min-width: 3.25rem !important;
  padding: 0 !important;
  aspect-ratio: 1 / 1;
  box-sizing: border-box;
  border-radius: 50% !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 8px 20px -4px rgba(0, 0, 0, 0.3),
    0 4px 8px rgba(0, 0, 0, 0.15),
    0 0 0 4px color-mix(in srgb, var(--primary-color) 12%, transparent);
  animation: support-widget-bounce 2.8s ease-in-out infinite;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    animation-play-state: paused;
    transform: scale(1.08) translateY(-2px);
    box-shadow:
      0 12px 24px -4px rgba(0, 0, 0, 0.35),
      0 6px 10px rgba(0, 0, 0, 0.18),
      0 0 0 6px color-mix(in srgb, var(--primary-color) 16%, transparent);
  }

  &:active {
    transform: scale(0.96);
  }
}

@keyframes support-widget-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  10% {
    transform: translateY(-8px);
  }
  20% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(-4px);
  }
  30% {
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .support-widget-button {
    animation: none;
  }
}

.support-widget-email {
  padding: 0.5rem 0.75rem;
  border-radius: var(--border-radius);
  background: var(--surface-hover);
  border: 1px solid var(--surface-border);
}
</style>
